import _ from "lodash";
import * as moment from "moment";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Statuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { DateHelper, Utility } from "../../../utils";
import { List } from "immutable";

interface OwnProps {
    api: Api;
    cancelLink: JSX.Element;
    onSubmit: (model: Forms.AdminEvent.EventData, body: any) => void;
}

interface OwnState {
    dateChanged: boolean;
    dateChangedConfirmed: boolean;
    loadingTZs: boolean;
    timeZones: M.Generic.ReactSelectOption[];
}

export class EditForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _dateChangeConfirm: Comp.Modal.ConfirmModal | null = null;
    private _previousStartDate: moment.Moment | undefined;
    private _subEvents: List<M.Event.SubEvent> = List();

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.AdminEvent.Event();
        this.state = {
            ...this.getInitialState(formModel),
            dateChanged: false,
            dateChangedConfirmed: false,
            loadingTZs: true,
            timeZones: [],
        };
    }

    public componentDidMount() {
        this.loadTimeZones();
    }

    public setFormData(data: M.Event.Event) {
        const formModel = this.getFormModelData<Forms.AdminEvent.EventData>();
        formModel.active = Statuses.isActive(data.statusId),
        formModel.address = Forms.Generic.AddressModelToForm(data);
        formModel.description = data.description || "";
        formModel.endDate = Utility.fromApiDate(data.endDate) || new Date();
        formModel.id = data.id;
        formModel.locationName = data.locationName || "";
        formModel.startDate = Utility.fromApiDate(data.startDate) || new Date();
        formModel.timezone = Utility.toReactSelectOption({}, data.timezone || "", data.timezone || "");
        formModel.title = data.title;

        this._previousStartDate = Utility.fromApiDateAsMoment(data.startDate);

        if (data.abstractSettings) {
            formModel.abstractsEnabled = true;
            formModel.abstractInvitationNotes = data.abstractSettings.invitationNotes;
            formModel.abstractSubmissionsOpen = Utility.fromApiDate(data.abstractSettings.submissionsOpenOnServer) || new Date();
        }

        this.updateFormModelData(formModel, () => this.fixModelTimeZone());
    }

    public render() {
        const formModel = this.getFormModelData<Forms.AdminEvent.EventData>();

        const timeZonesEditor = this.state.loadingTZs || !this.state.timeZones.length
            ? <Comp.Editors.FormField name="" label="Time zone" required>
                <Comp.Generic.LoadingIcon /> Loading timezones&hellip;
            </Comp.Editors.FormField>
            : <Comp.Editors.SelectField
                formComponent={this}
                name="timezone"
                select={{
                    filterOptions: (opts, search, currValues) => {
                        if (search && search.length) {
                            const s = search.toLowerCase();
                            const filtered = opts.filter((x) => (x.value + "").toLowerCase().indexOf(s) > -1);

                            for (const x of filtered) {
                                if ((x.value + "").toLowerCase() === s)
                                    x._rel = 1;
                                else if ((x.value + "").indexOf(s) === 0)
                                    x._rel = 2;
                                else
                                    x._rel = 3;
                            }

                            return _.orderBy(filtered, "_rel");
                        }
                        return opts;
                    },
                    options: this.state.timeZones,
                }}
            />;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onInputChange={(inputName) => this.handleInputChange(inputName)}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >

            {formModel.id > 0
                ? <Comp.Reusable.AdminSubEventsLoader
                    eventId={formModel.id}
                    onUpdated={(loading, subEvents) => this._subEvents = subEvents}
                />
                : null}

            <Comp.Modal.ConfirmModal
                headerClassName="info"
                onProceed={() => this.onDateChangeConfirmed()}
                title="Confirm date change"
                ref={(ref) => this._dateChangeConfirm = ref}
            >
                <p>Changing the start date will result in shifting the start dates for each sub event and session of this event.</p>
            </Comp.Modal.ConfirmModal>

            <Comp.Editors.CheckboxField formComponent={this} name="active" />
            <Comp.Editors.TextField formComponent={this} name="title" />

            {timeZonesEditor}

            <Comp.Editors.DateAndTimePickerField
                formComponent={this}
                name="startDate"
                hint={`Time is optional. If left blank, time indicates beginning of the day.
                       <br />Note: Specify time according to time zone selected above.`}
            />

            <Comp.Editors.DateAndTimePickerField
                formComponent={this}
                name="endDate"
                hint={`Time is optional. If left blank, time indicates end of the day.
                       <br />Note: Specify time according to time zone selected above.`}
            />

            <Comp.Editors.RichTextField formComponent={this} isAdmin name="description" />
            <Comp.Editors.TextField formComponent={this} name="locationName" />
            <Comp.Editors.AddressField formComponent={this} name="address" excludeAddressType />

            <hr />

            <h2>Abstracts</h2>

            <Comp.Editors.CheckboxField formComponent={this} name="abstractsEnabled" hint="Check this box to enable abstract submissions for this event." />

            <BS.Collapse in={formModel.abstractsEnabled}>
                <div>
                    <Comp.Editors.DateAndTimePickerField formComponent={this} name="abstractSubmissionsOpen" hint="Time zone is Central Standard Time (CST)" />
                    <Comp.Editors.RichTextField formComponent={this} isAdmin name="abstractInvitationNotes" />
                </div>
            </BS.Collapse>

            <hr />

            <Comp.Editors.SubmitField cancelLink={this.props.cancelLink} label="Save" showProcessing={this.state.formProcessing} />

        </Comp.Validation.ValidatedForm>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "startDate" && this._subEvents.size) {
            const newStartDate = moment(this.getFieldValue(inputName));

            // no need to track start date if a new event or event has no sub events
            if (!this._previousStartDate)
                return;

            if (newStartDate.isValid()) {
                const prevDate = this._previousStartDate.startOf("day").toDate().valueOf();
                const newDate = newStartDate.startOf("day").toDate().valueOf();

                if (prevDate !== newDate)
                    this.setState({ ...this.state, dateChanged: true }, () => {
                        this._previousStartDate = newStartDate;
                    });
            }
        }
    }

    private handleSubmit(model: Forms.AdminEvent.EventData) {
        // show confirm dialog if the start date changed
        if (this.state.dateChanged && !this.state.dateChangedConfirmed && this._dateChangeConfirm) {
            this._dateChangeConfirm.show(model);
            return;
        }

        const body = {
            abstractsEnabled: model.abstractsEnabled,
            abstractInvitationNotes: model.abstractInvitationNotes,
            abstractSubmissionsOpen: model.abstractSubmissionsOpen
                ? DateHelper.formatDateWithoutZone(model.abstractSubmissionsOpen)
                : null,
            active: model.active,
            address: Forms.Generic.AddressFormToPostData(model.address),
            description: model.description,
            endDate: DateHelper.formatDateWithoutZone(model.endDate || new Date()),
            id: model.id,
            locationName: model.locationName,
            startDate: DateHelper.formatDateWithoutZone(model.startDate || new Date()),
            timezone: model.timezone ? model.timezone.value : null,
            title: model.title,
        };

        this.props.onSubmit(model, body);
    }

    private loadTimeZones() {
        this.setState({ ...this.state, loadingTZs: true }, async () => {
            const result = await this.props.api.get(Api.endpoints.event.timezones);
            if (result.isSuccess && result.data) {
                const timeZones = result.data.map((x) => Utility.toReactSelectOption(x, `(${x.baseUtcOffsetDisplay}) - ${x.standardName}`, x.standardName));
                this.setState({ ...this.state, timeZones, loadingTZs: false }, () => this.fixModelTimeZone());
            }
            else
                this.setState({ ...this.state, loadingTZs: false });
        });
    }

    private onDateChangeConfirmed() {
        this.setState({
            ...this.state,
            dateChangedConfirmed: true,
        }, () => {
            if (this._dateChangeConfirm) {
                const model = this._dateChangeConfirm.getRef();
                this.handleSubmit(model);
            }
        });
    }

    private fixModelTimeZone() {
        const formModel = this.getFormModelData<Forms.AdminEvent.EventData>();
        if (formModel.timezone) {
            const tz = this.state.timeZones.filter((x) => x.value === formModel.timezone!.value);
            if (tz.length) {
                formModel.timezone = tz[0];
                this.updateFormModelData(formModel);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminEvent/edit/EditForm.tsx